import { default_survey_config, get_config } from "./config";
import type { ActiveDeploymentRegion, DeploymentRegion } from "./deployment";
import { sortByKey } from "./helpers";
import { getDeploymentRegion } from "./o11y/environment";
import {
  SurveyConfigOverride,
  SurveyContext,
  SurveyData,
  SurveyEnvironment,
  SurveyRevision,
  UrlParamsDef,
} from "./types";
import { TargetGroupWithRestricted } from "@/components/outreach/panel/types";
import { CompleteSegmentGroup } from "@/components/responses/segments/types";

export const get_latest_rev = <
  T extends { environment: SurveyEnvironment; created_at: string },
>(
  survey: { survey_revisions: T[] },
  includeDev = false
) => {
  if (survey.survey_revisions.length === 0) {
    throw new Error("No survey revisions found");
    // return null;
  }
  const sortedRevs = sortByKey(
    survey.survey_revisions,
    (r) => -new Date(r.created_at).getTime()
  );
  const revs = sortedRevs.filter((r) => includeDev || r.environment === "prod");
  if (revs.length === 0) {
    return survey.survey_revisions[0]; // if there are no prod revisions, might return a dev revision
  }
  return revs[0];
};

export const empty_revision: SurveyRevision = {
  title: "Empty Study",
  data: {
    title: "Empty Study",
    background: "",
    tone: "",
    external_title: "AI Interview",
    concepts: [],
    blocks: [],
  },
  created_at: "",
  updated_at: "",
  id: "",
  survey: "",
  environment: "prod",
  config: default_survey_config,
  analysis: null,
  version_id: null,
  published_at: null,
};

// Auto-generated link ids use a region-specific length (US=8, EU=10) so
// independently minted ids do not collide across stacks. Custom link ids are
// unrestricted and still need a shared registry for strict global uniqueness.
export const generatedLinkIdLengthByRegion = {
  us: 8,
  eu: 10,
} as const satisfies Record<ActiveDeploymentRegion, number>;

export const getGeneratedLinkIdLength = (
  deploymentRegion: DeploymentRegion
): number =>
  deploymentRegion === "missing"
    ? generatedLinkIdLengthByRegion.us
    : generatedLinkIdLengthByRegion[deploymentRegion];

// Works in both Node and the browser via the Web Crypto API (`crypto` is a
// global in Node 20+ and in all modern browsers). The surveys.link_id unique
// constraint catches the rare collision within a deployment.
export const genShortId = () => {
  const base62Chars =
    "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const bytes = new Uint8Array(getGeneratedLinkIdLength(getDeploymentRegion()));
  crypto.getRandomValues(bytes);
  return Array.from(
    bytes,
    (b) => base62Chars[Math.floor((b / 256) * base62Chars.length)]
  ).join("");
};

export const getStudyGuidePath = ({
  link,
  isUnifiedChatEnabled,
}: {
  link: string;
  isUnifiedChatEnabled: boolean;
}) =>
  isUnifiedChatEnabled ? `/p/${link}/study-guide` : `/p/${link}/edit/chat`;

// Non-editors land on the report page: the editor chat requires the Edit permission.
export const getStudyLandingPath = ({
  link,
  isDraft,
  canEdit,
  isUnifiedChatEnabled,
}: {
  link: string;
  isDraft: boolean;
  canEdit: boolean;
  isUnifiedChatEnabled: boolean;
}) =>
  isDraft && canEdit
    ? getStudyGuidePath({ link, isUnifiedChatEnabled })
    : `/p/${link}/analysis/report`;

// Client-only: shareable absolute URL for a study, honoring the same
// permission gate as the landing path.
export const getStudyCopyLink = (args: {
  link: string;
  isDraft: boolean;
  canEdit: boolean;
}) =>
  // Recipients may not have the unified chat, so shared links keep the creation chat.
  `${window.location.origin}${getStudyLandingPath({ ...args, isUnifiedChatEnabled: false })}`;

type BaseSurvey = {
  survey_revisions: {
    environment: SurveyEnvironment;
    created_at: string;
    data: SurveyData;
    config: SurveyConfigOverride;
  }[];
};
export const get_data_and_config = (survey: BaseSurvey) => {
  const rev = get_latest_rev(survey);
  const surveyData = rev.data;
  const config = get_config(rev.config);
  return { surveyData, config };
};

export const createSurveyContext = ({
  urlParams,
  targetGroups,
  segmentGroups,
  responseLimit,
}: {
  urlParams: UrlParamsDef;
  targetGroups: TargetGroupWithRestricted[];
  segmentGroups: CompleteSegmentGroup[];
  responseLimit?: number | null;
}): SurveyContext => {
  return {
    urlParams,
    targetGroups,
    segmentGroups,
    responseLimit,
  };
};
